import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Mail, Workflow } from 'lucide-react';
import { toast } from 'react-toastify';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';
import WhatsAppTemplateSelector from './WhatsAppTemplateSelector';
import {
  createCampaign,
  updateCampaign,
  addCampaignStep,
  deleteCampaignStep,
  getCampaignById,
  getTemplates,
  getStatusOptions,
  DripCampaign,
  DripCampaignStep,
  DripEmailTemplate,
  StatusOption
} from '@/actions/drip-campaigns';

interface CampaignFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: DripCampaign | null;
  onSuccess: () => void;
}

const CampaignFormModal: React.FC<CampaignFormModalProps> = ({
  isOpen,
  onClose,
  campaign,
  onSuccess
}) => {
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'steps'>('details');
  const [templates, setTemplates] = useState<DripEmailTemplate[]>([]);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [currentCampaignId, setCurrentCampaignId] = useState<number | null>(null);

  // Campaign Details Form
  const [campaignData, setCampaignData] = useState({
    campaign_name: '',
    description: '',
    goal: '',
    channel_type: 'email' as 'email' | 'whatsapp' | 'sms',
    campaign_type: 'drip' as 'drip' | 'status_change', // Changed from enrollment_type
    enrollment_type: 'manual' as 'manual' | 'auto_trigger', // Keep for backend compatibility
    auto_enrollment_trigger: null as any,
    send_time_preference: 'business_hours' as 'immediate' | 'business_hours' | 'optimal',
    business_hours_start: '09:00:00',
    business_hours_end: '17:00:00',
    timezone: 'Asia/Kolkata',
    allow_re_enrollment: false
  });

  // Status change configuration state (single email)
  const [statusChangeConfig, setStatusChangeConfig] = useState({
    target_status: '',      // opt_name (display + backward compat)
    target_status_uuid: '', // uuid (preferred for matching)
    template_id: 0
  });

  // Steps
  const [steps, setSteps] = useState<DripCampaignStep[]>([]);
  const [showAddStep, setShowAddStep] = useState(false);
  const [newStep, setNewStep] = useState({
    step_name: '',
    step_channel: 'email' as 'email' | 'whatsapp', // Channel for this specific step
    template_id: 0,
    integration_template_id_fk: 0,
    delay_value: 0,
    delay_unit: 'days' as 'minutes' | 'hours' | 'days' | 'weeks',
    delay_from: 'previous_step' as 'enrollment' | 'previous_step',
    parameter_mapping: {} as Record<string, string>
  });

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      loadStatusOptions();

      if (campaign) {
        setCurrentCampaignId(campaign.id);

        // Map enrollment_type to campaign_type
        const campaignType = campaign.enrollment_type === 'auto_trigger' ? 'status_change' : 'drip';

        setCampaignData({
          campaign_name: campaign.campaign_name,
          description: campaign.description || '',
          goal: campaign.goal || '',
          channel_type: campaign.channel_type || 'email',
          campaign_type: campaignType,
          enrollment_type: campaign.enrollment_type,
          auto_enrollment_trigger: campaign.auto_enrollment_trigger,
          send_time_preference: campaign.send_time_preference,
          business_hours_start: campaign.business_hours_start,
          business_hours_end: campaign.business_hours_end,
          timezone: campaign.timezone,
          allow_re_enrollment: campaign.allow_re_enrollment
        });
        setSteps(campaign.steps || []);

        // Load status change configuration if status_change type
        if (campaignType === 'status_change' && campaign.auto_enrollment_trigger) {
          const trigger = campaign.auto_enrollment_trigger;
          setStatusChangeConfig({
            target_status: trigger.trigger_config?.to_value || '',
            target_status_uuid: trigger.trigger_config?.to_uuid || '',
            template_id: trigger.trigger_config?.template_id || 0
          });
        }
      } else {
        // Reset for new campaign
        setCurrentCampaignId(null);
        setActiveTab('details');
        setCampaignData({
          campaign_name: '',
          description: '',
          goal: '',
          channel_type: 'email',
          campaign_type: 'drip',
          enrollment_type: 'manual',
          auto_enrollment_trigger: null,
          send_time_preference: 'business_hours',
          business_hours_start: '09:00:00',
          business_hours_end: '17:00:00',
          timezone: 'Asia/Kolkata',
          allow_re_enrollment: false
        });
        setSteps([]);
        setStatusChangeConfig({
          target_status: '',
          target_status_uuid: '',
          template_id: 0
        });
      }
    }
  }, [isOpen, campaign]);

  const loadTemplates = async () => {
    try {
      const response = await getTemplates();
      if (response.success && response.data) {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    }
  };

  const loadStatusOptions = async () => {
    try {
      const response = await getStatusOptions();
      if (response.success && response.data) {
        setStatusOptions(response.data);
      }
    } catch (error) {
      console.error('Error loading status options:', error);
      toast.error('Failed to load status options');
    }
  };

  const refreshCampaignData = async (campaignId: number) => {
    try {
      const response = await getCampaignById(campaignId);
      if (response.success && response.data) {
        setSteps(response.data.steps || []);
      }
    } catch (error) {
      console.error('Error refreshing campaign:', error);
    }
  };

  const handleSaveCampaign = async () => {
    if (!campaignData.campaign_name.trim()) {
      toast.error('Please enter a campaign name');
      return;
    }

    // Validate based on campaign type
    if (campaignData.campaign_type === 'status_change') {
      if (!statusChangeConfig.target_status) {
        toast.error('Please select the target status');
        return;
      }
      if (!statusChangeConfig.template_id) {
        toast.error('Please select an email template');
        return;
      }
    }

    try {
      setSaving(true);

      // Map campaign_type to enrollment_type for backend
      const enrollment_type: 'manual' | 'auto_trigger' = campaignData.campaign_type === 'status_change' ? 'auto_trigger' : 'manual';

      // Build the payload
      const payload = {
        ...campaignData,
        enrollment_type,
        auto_enrollment_trigger: campaignData.campaign_type === 'status_change' ? {
          trigger_type: 'status_change',
          trigger_config: {
            to_value: statusChangeConfig.target_status,
            to_uuid: statusChangeConfig.target_status_uuid,
            template_id: statusChangeConfig.template_id
          }
        } : null
      };

      // Remove campaign_type from payload (frontend only)
      delete (payload as any).campaign_type;

      if (campaign) {
        await updateCampaign(campaign.id, payload);
        toast.success('Campaign updated successfully');
        onSuccess();
        onClose();
      } else {
        // Create new campaign
        const response = await createCampaign(payload);
        toast.success('Campaign created successfully');

        // Get the newly created campaign ID
        if (response.success && response.data) {
          setCurrentCampaignId(response.data.id);

          // For drip campaigns, auto-switch to steps tab
          if (campaignData.campaign_type === 'drip') {
            setActiveTab('steps');
            toast.info('Now add email steps to your campaign');
          }
        }

        onSuccess();
      }
    } catch (error: any) {
      console.error('Error saving campaign:', error);
      toast.error(error.response?.data?.error || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleAddStep = async () => {
    const campaignId = currentCampaignId || campaign?.id;

    if (!campaignId) {
      toast.error('Please save the campaign first before adding steps');
      return;
    }

    // Validate based on STEP channel (not campaign channel) - allows mixing!
    const isEmailStep = newStep.step_channel === 'email';
    const isWhatsAppStep = newStep.step_channel === 'whatsapp';

    if (!newStep.step_name.trim()) {
      toast.error('Please enter a step name');
      return;
    }

    if (isEmailStep && !newStep.template_id) {
      toast.error('Please select an email template');
      return;
    }

    if (isWhatsAppStep && !newStep.integration_template_id_fk) {
      toast.error('Please select a WhatsApp template');
      return;
    }

    try {
      const stepOrder = steps.length + 1;
      await addCampaignStep(campaignId, {
        ...newStep,
        step_order: stepOrder,
        template_id_fk: isEmailStep ? newStep.template_id : null,
        integration_template_id_fk: isWhatsAppStep ? newStep.integration_template_id_fk : null
      });

      toast.success(`${isEmailStep ? 'Email' : 'WhatsApp'} step added successfully`);
      setShowAddStep(false);
      setNewStep({
        step_name: '',
        step_channel: 'email',
        template_id: 0,
        integration_template_id_fk: 0,
        delay_value: 0,
        delay_unit: 'days',
        delay_from: 'previous_step',
        parameter_mapping: {}
      });

      // Refresh steps list immediately
      await refreshCampaignData(campaignId);
      onSuccess();
    } catch (error: any) {
      console.error('Error adding step:', error);
      toast.error(error.response?.data?.error || 'Failed to add step');
    }
  };

  const handleDeleteStep = async (stepId: number) => {
    const campaignId = currentCampaignId || campaign?.id;
    if (!campaignId) return;

    if (!confirm('Are you sure you want to delete this step?')) return;

    try {
      await deleteCampaignStep(campaignId, stepId);
      toast.success('Step deleted successfully');

      // Refresh steps list immediately
      await refreshCampaignData(campaignId);
      onSuccess();
    } catch (error: any) {
      console.error('Error deleting step:', error);
      toast.error(error.response?.data?.error || 'Failed to delete step');
    }
  };

  if (!isOpen) return null;

  const inputClassName = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-brandColor";

  return (
    <SettingsSlideModal
      isOpen={isOpen}
      onClose={onClose}
      title={campaign ? 'Edit Campaign' : 'Create New Campaign'}
      subtitle={campaign ? 'Update your drip campaign settings' : 'Set up a new automated email campaign'}
      icon={<Workflow className="w-6 h-6" />}
      width="900px"
    >
      <>
        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-4 py-3 font-medium ${
              activeTab === 'details'
                ? 'text-brandColor border-b-2 border-brandColor'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            Campaign Details
          </button>
          {/* Show Steps tab only for drip campaigns */}
          {campaignData.campaign_type === 'drip' && (
            <button
              onClick={() => setActiveTab('steps')}
              className={`px-4 py-3 font-medium ${
                activeTab === 'steps'
                  ? 'text-brandColor border-b-2 border-brandColor'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
              disabled={!campaign && !currentCampaignId}
            >
              {campaignData.channel_type === 'whatsapp' ? '💬 WhatsApp Steps' : '📧 Email Steps'} ({steps.length})
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {activeTab === 'details' ? (
              <div className="space-y-6">
                {/* Campaign Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    value={campaignData.campaign_name}
                    onChange={(e) =>
                      setCampaignData({ ...campaignData, campaign_name: e.target.value })
                    }
                    className={inputClassName}
                    placeholder="e.g., New Lead Welcome Sequence"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={campaignData.description}
                    onChange={(e) =>
                      setCampaignData({ ...campaignData, description: e.target.value })
                    }
                    rows={3}
                    className={inputClassName}
                    placeholder="Describe the purpose of this campaign..."
                  />
                </div>

                {/* Goal */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Campaign Goal
                  </label>
                  <input
                    type="text"
                    value={campaignData.goal}
                    onChange={(e) => setCampaignData({ ...campaignData, goal: e.target.value })}
                    className={inputClassName}
                    placeholder="e.g., Convert leads to customers"
                  />
                </div>

                {/* Channel Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Primary Campaign Channel *
                  </label>
                  <select
                    value={campaignData.channel_type}
                    onChange={(e) =>
                      setCampaignData({
                        ...campaignData,
                        channel_type: e.target.value as 'email' | 'whatsapp' | 'sms'
                      })
                    }
                    className={inputClassName}
                  >
                    <option value="email">📧 Email</option>
                    <option value="whatsapp">💬 WhatsApp</option>
                    <option value="sms" disabled>📱 SMS (Coming Soon)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    ✨ New: You can now <span className="font-semibold">mix Email and WhatsApp steps</span> in the same campaign!
                    This sets the default channel for reporting purposes.
                  </p>
                </div>

                {/* Campaign Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Campaign Type *
                  </label>
                  <select
                    value={campaignData.campaign_type}
                    onChange={(e) =>
                      setCampaignData({
                        ...campaignData,
                        campaign_type: e.target.value as 'drip' | 'status_change'
                      })
                    }
                    className={inputClassName}
                  >
                    <option value="drip">Drip Campaign - Multi-step sequence</option>
                    <option value="status_change">Status Change - Single message on status change</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {campaignData.campaign_type === 'drip'
                      ? `Leads go through a sequence of multiple ${campaignData.channel_type === 'whatsapp' ? 'WhatsApp messages' : 'emails'}`
                      : `Send a single ${campaignData.channel_type === 'whatsapp' ? 'WhatsApp message' : 'email'} when lead status changes to selected status`}
                  </p>
                </div>

                {/* Status Change Configuration - Show only for status_change */}
                {campaignData.campaign_type === 'status_change' && (
                  <div className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                      <Workflow className="w-4 h-4" />
                      Status Change Configuration
                    </h4>

                    <div className="space-y-4">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Send email when lead status changes TO:
                      </p>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Target Status *
                        </label>
                        <select
                          value={statusChangeConfig.target_status_uuid || statusChangeConfig.target_status}
                          onChange={(e) => {
                            const selected = statusOptions.find(s => s.uuid === e.target.value);
                            setStatusChangeConfig({
                              ...statusChangeConfig,
                              target_status_uuid: selected?.uuid || '',
                              target_status: selected?.opt_name || e.target.value
                            });
                          }}
                          className={inputClassName}
                        >
                          <option value="">-- Select Status --</option>
                          {statusOptions.map((status) => (
                            <option key={status.opt_id} value={status.uuid}>
                              {status.opt_name}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          When lead status changes to this status (from any other status), send the email
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Email Template *
                        </label>
                        <select
                          value={statusChangeConfig.template_id}
                          onChange={(e) =>
                            setStatusChangeConfig({ ...statusChangeConfig, template_id: parseInt(e.target.value) })
                          }
                          className={inputClassName}
                        >
                          <option value="0">-- Select Template --</option>
                          {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.template_name} ({template.category})
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          This email will be sent once when the status changes
                        </p>
                      </div>

                      <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                        <p className="text-xs text-yellow-800 dark:text-yellow-200">
                          <strong>Example:</strong> Select "Hot" status and "Hot Lead Follow-up" template. When any lead's status changes to "Hot", they'll receive that email automatically.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Send Time Preference */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Send Time Preference
                  </label>
                  <select
                    value={campaignData.send_time_preference}
                    onChange={(e) =>
                      setCampaignData({
                        ...campaignData,
                        send_time_preference: e.target.value as any
                      })
                    }
                    className={inputClassName}
                  >
                    <option value="immediate">Send Immediately</option>
                    <option value="business_hours">Business Hours Only</option>
                    <option value="optimal">Optimal Send Time</option>
                  </select>
                </div>

                {/* Business Hours */}
                {campaignData.send_time_preference === 'business_hours' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Business Hours Start
                      </label>
                      <input
                        type="time"
                        value={campaignData.business_hours_start.substring(0, 5)}
                        onChange={(e) =>
                          setCampaignData({
                            ...campaignData,
                            business_hours_start: e.target.value + ':00'
                          })
                        }
                        className={inputClassName}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Business Hours End
                      </label>
                      <input
                        type="time"
                        value={campaignData.business_hours_end.substring(0, 5)}
                        onChange={(e) =>
                          setCampaignData({
                            ...campaignData,
                            business_hours_end: e.target.value + ':00'
                          })
                        }
                        className={inputClassName}
                      />
                    </div>
                  </div>
                )}

                {/* Timezone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Timezone
                  </label>
                  <select
                    value={campaignData.timezone}
                    onChange={(e) => setCampaignData({ ...campaignData, timezone: e.target.value })}
                    className={inputClassName}
                  >
                    <option value="Asia/Kolkata">India Standard Time (IST)</option>
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="UTC">UTC</option>
                    <option value="Europe/London">GMT</option>
                    <option value="Asia/Dubai">Gulf Standard Time (GST)</option>
                    <option value="Asia/Singapore">Singapore Time (SGT)</option>
                  </select>
                </div>

                {/* Allow Re-enrollment */}
                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={campaignData.allow_re_enrollment}
                      onChange={(e) =>
                        setCampaignData({ ...campaignData, allow_re_enrollment: e.target.checked })
                      }
                      className="h-4 w-4 text-brandColor rounded focus:ring-brandColor focus:outline-none"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Allow leads to re-enroll after completion
                    </span>
                  </label>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Existing Steps */}
                {steps.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Mail className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium mb-2">No steps added yet</p>
                    <p className="text-sm">
                      Add your first {campaignData.channel_type === 'whatsapp' ? 'WhatsApp' : 'email'} step to get started
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {steps.map((step) => (
                      <div
                        key={step.id}
                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-1 bg-brandColor/10 text-brandColor text-xs font-medium rounded">
                                Step {step.step_order}
                              </span>
                              <h4 className="font-semibold text-gray-900 dark:text-white">
                                {step.step_name}
                              </h4>
                              {campaignData.channel_type === 'whatsapp' && (
                                <span className="text-xs">💬</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Template: {step.template?.template_name || step.integrationTemplate?.element_name || 'N/A'}
                            </p>
                            {campaignData.channel_type === 'whatsapp' && step.integrationTemplate && (
                              <p className="text-sm text-gray-500 dark:text-gray-500">
                                {step.integrationTemplate.category} • {step.integrationTemplate.language}
                              </p>
                            )}
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Delay: {step.delay_value} {step.delay_unit} after{' '}
                              {step.delay_from === 'enrollment' ? 'enrollment' : 'previous step'}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteStep(step.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Step */}
                {!showAddStep ? (
                  <button
                    onClick={() => setShowAddStep(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-brandColor hover:text-brandColor transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Add {campaignData.channel_type === 'whatsapp' ? 'WhatsApp' : 'Email'} Step
                  </button>
                ) : (
                  <div className="p-4 border-2 border-brandColor rounded-lg space-y-4 bg-brandColor/5">
                    <h4 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <Mail className="w-5 h-5 text-brandColor" />
                      Add New Step
                    </h4>

                    {/* Step Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Step Name *
                      </label>
                      <input
                        type="text"
                        value={newStep.step_name}
                        onChange={(e) => setNewStep({ ...newStep, step_name: e.target.value })}
                        className={inputClassName}
                        placeholder="e.g., Welcome Email"
                      />
                    </div>

                    {/* Step Channel Selector - Allow mixing email and WhatsApp */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Step Channel *
                      </label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setNewStep({ ...newStep, step_channel: 'email', integration_template_id_fk: 0, parameter_mapping: {} })}
                          className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                            newStep.step_channel === 'email'
                              ? 'border-brandColor bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                          }`}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <Mail className="w-5 h-5" />
                            <span className="font-medium">Email</span>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewStep({ ...newStep, step_channel: 'whatsapp', template_id: 0 })}
                          className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                            newStep.step_channel === 'whatsapp'
                              ? 'border-brandColor bg-green-50 dark:bg-green-900/20'
                              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                          }`}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <Workflow className="w-5 h-5" />
                            <span className="font-medium">WhatsApp</span>
                          </div>
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        💡 Mix email and WhatsApp steps in the same campaign for multi-touch engagement
                      </p>
                    </div>

                    {/* Template - Conditional based on step channel */}
                    {newStep.step_channel === 'email' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Email Template *
                        </label>
                        <select
                          value={newStep.template_id}
                          onChange={(e) =>
                            setNewStep({ ...newStep, template_id: parseInt(e.target.value) })
                          }
                          className={inputClassName}
                        >
                          <option value={0}>Select a template</option>
                          {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.template_name} ({template.category})
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : newStep.step_channel === 'whatsapp' ? (
                      <WhatsAppTemplateSelector
                        value={newStep.integration_template_id_fk}
                        parameterMapping={newStep.parameter_mapping}
                        onChange={(template, paramMapping) => {
                          setNewStep({
                            ...newStep,
                            integration_template_id_fk: template?.id || 0,
                            parameter_mapping: paramMapping
                          });
                        }}
                      />
                    ) : null}

                    {/* Delay */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Delay Value
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={newStep.delay_value}
                          onChange={(e) =>
                            setNewStep({ ...newStep, delay_value: parseInt(e.target.value) || 0 })
                          }
                          className={inputClassName}
                        />
                      </div>
                      <div className="col-span-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Unit
                        </label>
                        <select
                          value={newStep.delay_unit}
                          onChange={(e) => setNewStep({ ...newStep, delay_unit: e.target.value as any })}
                          className={inputClassName}
                        >
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                          <option value="weeks">Weeks</option>
                        </select>
                      </div>
                      <div className="col-span-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          After
                        </label>
                        <select
                          value={newStep.delay_from}
                          onChange={(e) =>
                            setNewStep({ ...newStep, delay_from: e.target.value as any })
                          }
                          className={inputClassName}
                        >
                          <option value="enrollment">Enrollment</option>
                          <option value="previous_step">Previous Step</option>
                        </select>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddStep}
                        className="flex-1 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover flex items-center justify-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        Add Step
                      </button>
                      <button
                        onClick={() => setShowAddStep(false)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-900">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {campaign || currentCampaignId ? 'Close' : 'Cancel'}
            </button>
            {activeTab === 'details' && (
              <button
                onClick={handleSaveCampaign}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brandColor border border-transparent rounded-lg hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : campaign ? 'Update Campaign' : 'Create Campaign'}
              </button>
            )}
          </div>
        </div>
      </>
    </SettingsSlideModal>
  );
};

export default CampaignFormModal;
